import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import supabase from '../lib/supabase';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiMapPin, FiClock, FiFilter, FiSearch, FiTag, FiExternalLink, FiUsers, FiRefreshCw } = FiIcons;

const EventsAggregated = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    state: 'all',
    city: '',
    tags: [],
    dateRange: 'upcoming'
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Available states (will be populated from actual data)
  const [availableStates, setAvailableStates] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);

  useEffect(() => {
    fetchEvents();
    fetchFilterOptions();
  }, [filters]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('events')
        .select(`
          *,
          orgs (
            name,
            type,
            website
          )
        `)
        .eq('visibility', 'public')
        .neq('status', 'cancelled')
        .order('start_time', { ascending: true });

      // Apply filters
      if (filters.state !== 'all') {
        query = query.eq('state', filters.state);
      }

      if (filters.city) {
        query = query.ilike('city', `%${filters.city}%`);
      }

      if (filters.tags.length > 0) {
        query = query.overlaps('tags', filters.tags);
      }

      // Date range filter
      const now = new Date();
      if (filters.dateRange === 'upcoming') {
        query = query.gte('start_time', now.toISOString());
      } else if (filters.dateRange === 'today') {
        const endOfDay = new Date(now);
        endOfDay.setHours(23, 59, 59, 999);
        query = query.gte('start_time', now.toISOString())
                   .lte('start_time', endOfDay.toISOString());
      } else if (filters.dateRange === 'week') {
        const weekFromNow = new Date(now);
        weekFromNow.setDate(now.getDate() + 7);
        query = query.gte('start_time', now.toISOString())
                   .lte('start_time', weekFromNow.toISOString());
      }

      // Search filter
      if (searchTerm) {
        query = query.or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,city.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query.limit(100);

      if (error) throw error;

      setEvents(data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      // Get available states
      const { data: statesData } = await supabase
        .from('events')
        .select('state')
        .eq('visibility', 'public')
        .not('state', 'is', null);

      const states = [...new Set(statesData?.map(e => e.state))].sort();
      setAvailableStates(states);

      // Get available tags
      const { data: tagsData } = await supabase
        .from('events')
        .select('tags')
        .eq('visibility', 'public');

      const allTags = tagsData?.flatMap(e => e.tags || []) || [];
      const uniqueTags = [...new Set(allTags)].sort();
      setAvailableTags(uniqueTags);

    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleTagToggle = (tag) => {
    setFilters(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getTagColor = (tag) => {
    const colors = {
      'phone-bank': 'bg-blue-100 text-blue-800',
      'canvass': 'bg-green-100 text-green-800',
      'fundraiser': 'bg-purple-100 text-purple-800',
      'training': 'bg-orange-100 text-orange-800',
      'rally': 'bg-red-100 text-red-800',
      'meeting': 'bg-gray-100 text-gray-800',
      'volunteer': 'bg-yellow-100 text-yellow-800'
    };
    return colors[tag] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-patriot-navy mb-4">
            Republican Events Nationwide
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover GOP events, rallies, fundraisers, and volunteer opportunities across America.
            Updated automatically from official Republican sources.
          </p>
        </motion.div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            {/* Search */}
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
              />
            </div>

            {/* State Filter */}
            <select
              value={filters.state}
              onChange={(e) => handleFilterChange('state', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
            >
              <option value="all">All States</option>
              {availableStates.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>

            {/* City Filter */}
            <input
              type="text"
              placeholder="City"
              value={filters.city}
              onChange={(e) => handleFilterChange('city', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
            />

            {/* Date Range Filter */}
            <select
              value={filters.dateRange}
              onChange={(e) => handleFilterChange('dateRange', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
            >
              <option value="upcoming">Upcoming</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>

          {/* Tag Filters */}
          <div className="flex flex-wrap gap-2">
            {availableTags.map(tag => (
              <button
                key={tag}
                onClick={() => handleTagToggle(tag)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors duration-200 ${
                  filters.tags.includes(tag)
                    ? 'bg-patriot-red text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Events List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <SafeIcon icon={FiRefreshCw} className="h-8 w-8 text-patriot-red animate-spin" />
            <span className="ml-2 text-gray-600">Loading events...</span>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12">
            <SafeIcon icon={FiCalendar} className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No events found</h3>
            <p className="text-gray-600">Try adjusting your filters or search terms.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {events.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
              >
                <div className="p-6">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                    <div className="flex-1">
                      {/* Event Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-semibold text-patriot-navy mb-2">
                            {event.title}
                          </h3>
                          {event.orgs && (
                            <p className="text-sm text-gray-600 mb-2">
                              Organized by {event.orgs.name}
                            </p>
                          )}
                        </div>
                        {event.url && (
                          <a
                            href={event.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center text-patriot-red hover:text-patriot-red-dark"
                          >
                            <SafeIcon icon={FiExternalLink} className="h-4 w-4 mr-1" />
                            <span className="text-sm">View Details</span>
                          </a>
                        )}
                      </div>

                      {/* Event Details */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="flex items-center text-gray-600">
                          <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-2" />
                          <span className="text-sm">{formatDate(event.start_time)}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <SafeIcon icon={FiClock} className="h-4 w-4 mr-2" />
                          <span className="text-sm">
                            {formatTime(event.start_time)}
                            {event.end_time && ` - ${formatTime(event.end_time)}`}
                          </span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <SafeIcon icon={FiMapPin} className="h-4 w-4 mr-2" />
                          <span className="text-sm">
                            {event.city}, {event.state}
                          </span>
                        </div>
                      </div>

                      {/* Venue */}
                      {event.venue_name && (
                        <p className="text-sm text-gray-700 mb-3">
                          <strong>Venue:</strong> {event.venue_name}
                          {event.address && `, ${event.address}`}
                        </p>
                      )}

                      {/* Description */}
                      {event.description && (
                        <p className="text-gray-700 mb-4 line-clamp-2">
                          {event.description}
                        </p>
                      )}

                      {/* Tags */}
                      {event.tags && event.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {event.tags.map(tag => (
                            <span
                              key={tag}
                              className={`px-2 py-1 rounded-full text-xs font-medium ${getTagColor(tag)}`}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex flex-wrap gap-2">
                        {event.ticket_url && (
                          <a
                            href={event.ticket_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-patriot-red text-white px-4 py-2 rounded-lg hover:bg-patriot-red-dark transition-colors duration-200 text-sm"
                          >
                            Get Tickets
                          </a>
                        )}
                        <button
                          onClick={() => {
                            // Generate ICS file for calendar
                            const icsContent = generateICS(event);
                            downloadICS(icsContent, `${event.title}.ics`);
                          }}
                          className="border border-patriot-red text-patriot-red px-4 py-2 rounded-lg hover:bg-patriot-red hover:text-white transition-colors duration-200 text-sm"
                        >
                          Add to Calendar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Utility functions
function generateICS(event) {
  const startDate = new Date(event.start_time);
  const endDate = event.end_time ? new Date(event.end_time) : new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
  
  const formatICSDate = (date) => {
    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  };

  return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//GOP Events//GOP Events Aggregator//EN
BEGIN:VEVENT
UID:${event.id}@gopevents.com
DTSTAMP:${formatICSDate(new Date())}
DTSTART:${formatICSDate(startDate)}
DTEND:${formatICSDate(endDate)}
SUMMARY:${event.title}
DESCRIPTION:${event.description || ''}
LOCATION:${event.venue_name || ''}${event.address ? ', ' + event.address : ''}${event.city ? ', ' + event.city : ''}${event.state ? ', ' + event.state : ''}
URL:${event.url || ''}
END:VEVENT
END:VCALENDAR`;
}

function downloadICS(content, filename) {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export default EventsAggregated;